import React, { useRef, useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { TouchableOpacity, Text, StyleSheet } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { FontFamily } from "../../constants/fonts";
import { useTheme } from "../../theme";

interface WebDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  error?: boolean;
  disabled?: boolean;
  iconColor?: string;
  disabledIconColor?: string;
  /** When provided, overrides the trigger button text (e.g. for date range display) */
  displayText?: string;
  /** When provided, highlights all dates between start and end in the calendar */
  highlightedRange?: { start: Date; end: Date } | null;
  /**
   * When provided (non-empty), restricts selectable calendar dates to this exact set of
   * ISO date strings (YYYY-MM-DD) — in addition to the minimumDate/maximumDate bounds.
   * Prevents enabling every date in a continuous min–max range when the actual valid
   * dates are a non-contiguous union (e.g. Period 1 of 2025 + Period 1 of 2026 only,
   * not every date from P1 2025 through P1 2026). Falls back to min/max-only behavior
   * when omitted or empty.
   */
  enabledDates?: string[];
  /** Controls the visual theme of the calendar popup and trigger button. Defaults to "light". */
  colorScheme?: "light" | "dark";
}

// Minimum width for the calendar popup so the 7-column day grid (28px cells + gaps/padding)
// always has enough room and the date numbers never render outside/clipped by the box.
const MIN_CALENDAR_WIDTH = 240;

const WebDatePicker: React.FC<WebDatePickerProps> = ({
  value,
  onChange,
  placeholder = "Select date",
  minimumDate,
  maximumDate,
  error = false,
  disabled = false,
  iconColor,
  disabledIconColor,
  displayText,
  highlightedRange,
  enabledDates,
  colorScheme = "light",
}) => {
  // Theme palette — light (default) or dark
  const { colors: themeColors, resolvedTheme } = useTheme();
  const isAppDark = resolvedTheme === "dark";
  const darkPalette = {
    bg:           "#000000",
    bgAlt:        "#111111",
    border:       "#333333",
    text:         "#FFFFFF",
    textMuted:    "#555555",
    selectedBg:   "#333333",
    selectedText: "#FFFFFF",
    selectedBorder: "#FFFFFF",
    rangeBg:      "#333333",
    todayBorder:  "#555555",
    shadow:       "0 4px 8px rgba(0,0,0,0.3)",
    triggerBg:    "#000000",
    triggerBorder: "#555555",
    triggerText:  "#FFFFFF",
    placeholderText: "#999999",
    disabledBg:   "#1a1a1a",
    disabledText: "#666666",
    iconColor:    "#0066CC",
    disabledIconColor: "#666666",
  };
  // In dark app theme use the grey theme surfaces instead of pure black
  const greyDarkPalette = {
    ...darkPalette,
    bg:           themeColors.background.elevated,
    bgAlt:        themeColors.background.surface,
    border:       themeColors.border.default,
    text:         themeColors.text.primary,
    textMuted:    themeColors.text.disabled,
    selectedBg:   themeColors.input.border,
    rangeBg:      themeColors.input.border,
    todayBorder:  themeColors.text.disabled,
    triggerBg:    themeColors.background.elevated,
    triggerBorder: themeColors.border.default,
    triggerText:  themeColors.text.primary,
    placeholderText: themeColors.text.secondary,
    disabledBg:   themeColors.background.surface,
    disabledText: themeColors.text.disabled,
    disabledIconColor: themeColors.text.disabled,
  };
  const cal = colorScheme === "dark"
    ? (isAppDark ? greyDarkPalette : darkPalette)
    : {
        bg:           "#ffffff",
        bgAlt:        "#f5f7fa",
        border:       "#d0d7e2",
        text:         "#1a1a1a",
        textMuted:    "#9aa3b0",
        selectedBg:   "#0B5FC6",
        selectedText: "#ffffff",
        selectedBorder: "#0B5FC6",
        rangeBg:      "#dbeafe",
        todayBorder:  "#0B5FC6",
        shadow:       "0 4px 12px rgba(0,0,0,0.12)",
        triggerBg:    "white",
        triggerBorder: "#ced4da",
        triggerText:  "#333",
        placeholderText: "#999",
        disabledBg:   "#f5f5f5",
        disabledText: "#999",
        iconColor:    "#0066CC",
        disabledIconColor: "#999",
      };

  const resolvedIconColor = iconColor ?? cal.iconColor;
  const resolvedDisabledIconColor = disabledIconColor ?? cal.disabledIconColor;
  const buttonRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [viewingMonth, setViewingMonth] = useState(
    (value || new Date()).getMonth()
  );
  const [viewingYear, setViewingYear] = useState(
    (value || new Date()).getFullYear()
  );
  const [showYearList, setShowYearList] = useState(false);
  const [showMonthList, setShowMonthList] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const [portalStyle, setPortalStyle] = useState<Record<string, any>>({});
  const yearScrollRef = useRef<HTMLDivElement>(null);

  // Scroll selected year into view when list opens
  useEffect(() => {
    if (showYearList && yearScrollRef.current) {
      const selected = yearScrollRef.current.querySelector('[data-selected="true"]') as HTMLElement | null;
      if (selected) {
        selected.scrollIntoView({ block: 'center' });
      }
    }
  }, [showYearList]);

  // Exact-match set of allowed ISO dates (see enabledDates prop doc above).
  const enabledDatesSet = useMemo(() => {
    if (!enabledDates || enabledDates.length === 0) return null;
    return new Set(enabledDates);
  }, [enabledDates]);

  const today = new Date();

  // Keep viewing month/year in sync when external value changes
  useEffect(() => {
    const newDate = value || new Date();
    setViewingMonth(newDate.getMonth());
    setViewingYear(newDate.getFullYear());
  }, [value]);

  // Handlers to navigate months and adjust year when wrapping
  const handlePrevMonth = () => {
    setViewingMonth((prevMonth) => {
      if (prevMonth === 0) {
        setViewingYear((y) => y - 1);
        return 11;
      }
      return prevMonth - 1;
    });
  };

  const handleNextMonth = () => {
    setViewingMonth((prevMonth) => {
      if (prevMonth === 11) {
        setViewingYear((y) => y + 1);
        return 0;
      }
      return prevMonth + 1;
    });
  };

  const formatDate = (date: Date | null): string =>
    date?.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }) || "";

  const createCalendar = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const calendar = [];
    let day = 1;

    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDay) || day > daysInMonth) {
          week.push(null);
        } else {
          week.push(day++);
        }
      }
      calendar.push(week);
      if (day > daysInMonth) {
        break;
      }
    }
    return calendar;
  };

  const calendar = createCalendar(viewingYear, viewingMonth);
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      // compute initial position when opened
      try {
        const rect = buttonRef.current?.getBoundingClientRect?.();
        if (rect) {
          const windowHeight = window.innerHeight;
          const spaceBelow = windowHeight - rect.bottom;
          const spaceAbove = rect.top;
          const estimatedCalendarHeight = 320; // conservative estimate
          setPositionAbove(spaceBelow < estimatedCalendarHeight && spaceAbove > spaceBelow);
        }
      } catch (e) {
        // ignore
      }
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPicker]);

  // Create a portal element on open and compute fixed position so calendar is not clipped
  useEffect(() => {
    const estimatedCalendarHeight = 320;
    let portalEl: HTMLDivElement | null = null;

    const createPortal = () => {
      try {
        portalEl = document.createElement("div");
        portalEl.setAttribute("data-web-datepicker-portal", "");
        // Ensure the portal container sits above other stacking contexts
        portalEl.style.position = 'fixed';
        portalEl.style.top = '0';
        portalEl.style.left = '0';
        portalEl.style.width = '100%';
        portalEl.style.height = '0';
        portalEl.style.zIndex = '2147483647';
        portalEl.style.pointerEvents = 'none';
        document.body.appendChild(portalEl);
        portalContainerRef.current = portalEl;

        const rect = buttonRef.current?.getBoundingClientRect?.();
        if (rect) {
          // enable pointer events for the actual calendar overlay through its child
          portalEl.style.pointerEvents = 'auto';
          const windowHeight = window.innerHeight;
          const spaceBelow = windowHeight - rect.bottom;
          const spaceAbove = rect.top;
          const shouldAbove = spaceBelow < estimatedCalendarHeight && spaceAbove > spaceBelow;
          setPositionAbove(shouldAbove);

          // Use fixed positioning relative to viewport
          const top = shouldAbove ? Math.max(8, rect.top - estimatedCalendarHeight - 5) : rect.bottom + 5;
          const left = rect.left;
          const width = Math.max(rect.width || 200, MIN_CALENDAR_WIDTH);
          setPortalStyle({ position: "fixed", top: `${top}px`, left: `${left}px`, width: `${width}px`, minWidth: `${MIN_CALENDAR_WIDTH}px`, zIndex: 10000 });
        }
      } catch (e) {
        // ignore
      }
    };

    const removePortal = () => {
      try {
        if (portalContainerRef.current && portalContainerRef.current.parentNode) {
          portalContainerRef.current.parentNode.removeChild(portalContainerRef.current);
        }
      } catch (e) {}
      portalContainerRef.current = null;
      setPortalStyle({});
    };

    if (showPicker) createPortal();
    else removePortal();

    const recompute = () => {
      try {
        const rect = buttonRef.current?.getBoundingClientRect?.();
        if (rect && portalContainerRef.current) {
          const windowHeight = window.innerHeight;
          const spaceBelow = windowHeight - rect.bottom;
          const spaceAbove = rect.top;
          const shouldAbove = spaceBelow < estimatedCalendarHeight && spaceAbove > spaceBelow;
          setPositionAbove(shouldAbove);
          const top = shouldAbove ? Math.max(8, rect.top - estimatedCalendarHeight - 5) : rect.bottom + 5;
          const left = rect.left;
          const width = Math.max(rect.width || 200, MIN_CALENDAR_WIDTH);
          setPortalStyle({ position: "fixed", top: `${top}px`, left: `${left}px`, width: `${width}px`, minWidth: `${MIN_CALENDAR_WIDTH}px`, zIndex: 10000 });
        }
      } catch (e) {}
    };

    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
      removePortal();
    };
  }, [showPicker]);

  const isSelectedDate = (day: number | null) =>
    day &&
    value &&
    value.getDate() === day &&
    value.getMonth() === viewingMonth &&
    value.getFullYear() === viewingYear;

  const isInHighlightedRange = (day: number | null): boolean => {
    if (!day || !highlightedRange) return false;
    const cellDate = new Date(viewingYear, viewingMonth, day);
    cellDate.setHours(0, 0, 0, 0);
    const start = new Date(highlightedRange.start);
    start.setHours(0, 0, 0, 0);
    const end = new Date(highlightedRange.end);
    end.setHours(0, 0, 0, 0);
    return cellDate >= start && cellDate <= end;
  };

  const isToday = (day: number | null) =>
    day &&
    today.getDate() === day &&
    today.getMonth() === viewingMonth &&
    today.getFullYear() === viewingYear;

  const isDisabledDate = (day: number | null) => {
    if (!day) {
      return true;
    }
    const date = new Date(viewingYear, viewingMonth, day);
    if ((minimumDate && date < minimumDate) || (maximumDate && date > maximumDate)) {
      return true;
    }
    // Exact-match restriction: when a non-empty enabledDates set is provided, only dates
    // present in that set are selectable — prevents enabling every date in the min–max
    // span when the valid dates are a non-contiguous union (e.g. multiple years × one period).
    if (enabledDatesSet && enabledDatesSet.size > 0) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const dayStr = String(date.getDate()).padStart(2, "0");
      return !enabledDatesSet.has(`${year}-${month}-${dayStr}`);
    }
    return false;
  };

  const handleDateClick = (day: number | null) => {
    if (!day || isDisabledDate(day)) {
      return;
    }
    onChange(new Date(viewingYear, viewingMonth, day));
    setShowPicker(false);
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div ref={buttonRef} style={{ width: "100%" }}>
        <TouchableOpacity
          style={[
            styles.pickerButton,
            { backgroundColor: cal.triggerBg, borderColor: error ? "#dc3545" : cal.triggerBorder },
            disabled && { backgroundColor: cal.disabledBg, borderColor: cal.border },
          ]}
          onPress={() => !disabled && setShowPicker(true)}
          disabled={disabled}
        >
          <Text
            style={[
              styles.dateText,
              { color: cal.triggerText },
              (!value && !displayText) && { color: cal.placeholderText },
              disabled && { color: cal.disabledText },
            ]}
          >
            {displayText || (value ? formatDate(value) : placeholder)}
          </Text>
          <MaterialIcons
            name="calendar-today"
            size={20}
            color={disabled ? resolvedDisabledIconColor : resolvedIconColor}
            style={{ marginLeft: 8 }}
          />
        </TouchableOpacity>
      </div>

      {showPicker && portalContainerRef.current && ReactDOM.createPortal(
         <div ref={calendarRef} style={{ ...(portalStyle || {}), backgroundColor: cal.bg, border: `1px solid ${cal.border}`, borderRadius: 4, boxShadow: cal.shadow }}>
           {/* Header */}
           <div
             style={{
               display: "flex",
               justifyContent: "space-between",
               padding: "6px 10px",
               borderBottom: `1px solid ${cal.border}`,
               fontFamily: FontFamily.regular as string,
               alignItems: "center",
               color: cal.text,
             }}
           >
            <span
              style={{ cursor: "pointer", color: cal.text, fontWeight: "bold", fontSize: "16px" }}
              onClick={handlePrevMonth}
            >
              ❮
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", position: "relative" }}>
              {/* Month button + inline dropdown */}
              <div style={{ position: "relative" }}>
                <div
                  onClick={() => { setShowMonthList(v => !v); setShowYearList(false); }}
                  style={{
                    fontWeight: "bold",
                    fontSize: "13px",
                    fontFamily: FontFamily.regular as string,
                    border: `1px solid ${cal.border}`,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    cursor: "pointer",
                    backgroundColor: cal.bg,
                    color: cal.text,
                    userSelect: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {monthNames[viewingMonth]}
                  <span style={{ fontSize: "9px", color: cal.text }}>▼</span>
                </div>
                {showMonthList && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: cal.bg,
                      border: `1px solid ${cal.border}`,
                      borderRadius: "4px",
                      boxShadow: cal.shadow,
                      zIndex: 1000000,
                      maxHeight: "160px",
                      overflowY: "auto",
                      minWidth: "100px",
                    }}
                  >
                    {monthNames.map((name, idx) => (
                      <div
                        key={name}
                        onClick={() => { setViewingMonth(idx); setShowMonthList(false); }}
                        style={{
                          padding: "5px 10px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontFamily: FontFamily.regular as string,
                          backgroundColor: idx === viewingMonth ? cal.selectedBg : cal.bg,
                          color: idx === viewingMonth ? cal.selectedText : cal.text,
                        }}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Year button + inline dropdown */}
              <div style={{ position: "relative" }}>
                <div
                  onClick={() => { setShowYearList(v => !v); setShowMonthList(false); }}
                  style={{
                    fontWeight: "bold",
                    fontSize: "13px",
                    fontFamily: FontFamily.regular as string,
                    border: `1px solid ${cal.border}`,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    cursor: "pointer",
                    backgroundColor: cal.bg,
                    color: cal.text,
                    userSelect: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {viewingYear}
                  <span style={{ fontSize: "9px", color: cal.text }}>▼</span>
                </div>
                {showYearList && (
                  <div
                    ref={yearScrollRef}
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      backgroundColor: cal.bg,
                      border: `1px solid ${cal.border}`,
                      borderRadius: "4px",
                      boxShadow: cal.shadow,
                      zIndex: 1000000,
                      maxHeight: "160px",
                      overflowY: "auto",
                      minWidth: "64px",
                    }}
                  >
                    {Array.from(
                      { length: 51 },
                      (_, i) => today.getFullYear() - 25 + i
                    ).map((yr) => (
                      <div
                        key={yr}
                        data-selected={yr === viewingYear ? "true" : "false"}
                        onClick={() => { setViewingYear(yr); setShowYearList(false); }}
                        style={{
                          padding: "5px 10px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontFamily: FontFamily.regular as string,
                          backgroundColor: yr === viewingYear ? cal.selectedBg : cal.bg,
                          color: yr === viewingYear ? cal.selectedText : cal.text,
                        }}
                      >
                        {yr}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <span
              style={{ cursor: "pointer", color: cal.text, fontWeight: "bold", fontSize: "16px" }}
              onClick={handleNextMonth}
            >
              ❯
            </span>
          </div>

          {/* Day Headers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              backgroundColor: cal.bgAlt,
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "11px",
              padding: "6px 0",
              gap: "1px",
              fontFamily: FontFamily.regular as string,
              color: cal.text,
            }}
          >
            {dayNames.map((day) => (
              <div key={day} style={{ padding: "0 1px", overflow: "hidden", textOverflow: "ellipsis", color: cal.text }}>{day}</div>
            ))}
          </div>

          {/* Dates */}
          <div style={{ padding: "4px", backgroundColor: cal.bg }}>
            {calendar.map((week, wi) => (
              <div
                key={wi}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  textAlign: "center",
                  gap: "1px",
                }}
              >
                {week.map((day, di) => {
                  const selected = isSelectedDate(day);
                  const todayMark = isToday(day);
                  const disabledDay = isDisabledDate(day);
                  const inRange = isInHighlightedRange(day);
                  return (
                    <div
                      key={di}
                      onClick={() => !disabledDay && handleDateClick(day)}
                      style={{
                        padding: "2px",
                        cursor: disabledDay ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: FontFamily.regular as string,
                        backgroundColor: cal.bg,
                      }}
                    >
                      {day ? (
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: selected
                              ? cal.selectedBg
                              : inRange
                              ? cal.rangeBg
                              : "transparent",
                            border: selected
                              ? `1.5px solid ${cal.selectedBorder}`
                              : todayMark
                              ? `1px solid ${cal.todayBorder}`
                              : "none",
                            color: selected
                              ? cal.selectedText
                              : disabledDay
                              ? cal.textMuted
                              : cal.text,
                            fontWeight: selected ? "700" : inRange ? "600" : "400",
                            fontSize: "12px",
                            fontFamily: FontFamily.regular as string,
                          }}
                        >
                          {day}
                        </div>
                      ) : ""}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: 8,
              borderTop: `1px solid ${cal.border}`,
              backgroundColor: cal.bg,
              fontFamily: FontFamily.regular as string,
            }}
          >
            <span
              style={{ cursor: "pointer", color: cal.text, fontFamily: FontFamily.regular as string, fontSize: "14px" }}
              onClick={() => {
                onChange(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
                setShowPicker(false);
              }}
            >
              Today
            </span>
            <span
              style={{ cursor: "pointer", color: cal.text, fontFamily: FontFamily.regular as string, fontSize: "14px" }}
              onClick={() => setShowPicker(false)}
            >
              Close
            </span>
          </div>
        </div>,
        portalContainerRef.current
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  pickerButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#000000",
    padding: 10,
    borderWidth: 1,
    borderRadius: 4,
    borderColor: "#555555",
    width: "100%",
    minHeight: 40,
  },
  dateText: { fontSize: 14, color: "#FFFFFF", fontFamily: FontFamily.regular },
});

export default WebDatePicker;
